import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { ExternalLink, Search } from 'lucide-react';

import { settingsApi } from '../../../api/settings.js';
import { rolesApi } from '../../../api/roles.js';
import { aiApi } from '../../../api/ai.js';
import { useAuth } from '../../../auth/AuthProvider.jsx';
import { PageHeader } from '../../../components/ui/PageHeader.jsx';
import { Card } from '../../../components/ui/Card.jsx';
import { Button } from '../../../components/ui/Button.jsx';
import { Input } from '../../../components/ui/Input.jsx';
import { Skeleton } from '../../../components/ui/Skeleton.jsx';
import { SettingField } from '../../../components/settings/SettingField.jsx';
import { SECTIONS } from './settingsSchema.js';
import { ApiError } from '../../../api/client.js';
import { t } from '../../../lib/i18n.js';

// A section holds either flat `fields` or sub-`groups` of fields.
function sectionFields(section) {
  return section.fields || (section.groups || []).flatMap((g) => g.fields);
}

const FIELD_BY_KEY = {};
SECTIONS.forEach((s) => sectionFields(s).forEach((f) => { FIELD_BY_KEY[f.key] = f; }));

export function SettingsPage() {
  const { hasPermission } = useAuth();
  const canUpdate = hasPermission('settings:update');
  const queryClient = useQueryClient();

  const canConfigureAi = hasPermission('ai:configure');
  const [activeTab, setActiveTab] = useState(SECTIONS[0].id);
  const [draft, setDraft] = useState({});
  const [search, setSearch] = useState('');

  const settingsQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => settingsApi.list({ limit: 100 })
  });
  const rolesQuery = useQuery({ queryKey: ['roles', 'all'], queryFn: () => rolesApi.list({ limit: 100 }) });
  // Catalog feeds the model selectors. Optional: falls back to text input.
  const modelsQuery = useQuery({ queryKey: ['ai', 'models'], enabled: canConfigureAi, queryFn: () => aiApi.models() });

  const modelOptions = useMemo(() => {
    const catalog = (modelsQuery.data && modelsQuery.data.catalog) || [];
    const byKind = { chat: [], realtime: [], embedding: [], transcription: [] };
    for (const m of catalog) {
      if (m.enabled === false || !byKind[m.kind]) continue;
      byKind[m.kind].push({ value: m.id, label: `${m.label} (${m.id})` });
    }
    return byKind;
  }, [modelsQuery.data]);

  const map = useMemo(() => {
    const items = (settingsQuery.data && settingsQuery.data.items) || [];
    const m = {};
    for (const it of items) m[it.key] = it;
    return m;
  }, [settingsQuery.data]);

  const roleOptions = useMemo(() => {
    const items = (rolesQuery.data && rolesQuery.data.items) || [];
    return items.map((r) => ({ value: r.name, label: `${r.label} (${r.name})` }));
  }, [rolesQuery.data]);

  const baseValue = (field) => {
    if (field.control === 'password') return '';
    const s = map[field.key];
    return s ? s.value : undefined;
  };

  const currentValue = (field) => (field.key in draft ? draft[field.key] : baseValue(field));

  const isDirty = (key) => {
    const field = FIELD_BY_KEY[key];
    if (!field || !(key in draft)) return false;
    if (field.control === 'password') return typeof draft[key] === 'string' && draft[key].length > 0;
    return JSON.stringify(draft[key]) !== JSON.stringify(baseValue(field));
  };

  const dirtyKeys = useMemo(() => Object.keys(draft).filter(isDirty), [draft, map]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      await Promise.all(dirtyKeys.map((key) => {
        const s = map[key];
        return settingsApi.upsert(key, { value: draft[key], type: s ? s.type : undefined });
      }));
    },
    onSuccess: () => {
      toast.success('Impostazioni salvate.');
      setDraft({});
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : t('common.error'))
  });

  const section = SECTIONS.find((s) => s.id === activeTab) || SECTIONS[0];

  const q = search.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!q) return [];
    return SECTIONS.map((s) => {
      const fields = sectionFields(s).filter((f) =>
        f.label.toLowerCase().includes(q)
        || (f.help || '').toLowerCase().includes(q)
        || f.key.toLowerCase().includes(q)
      );
      return fields.length ? { section: s, fields } : null;
    }).filter(Boolean);
  }, [q]);

  const renderFields = (fields) => (
    <div className="flex flex-col divide-y divide-border/60">
      {fields.map((field) => {
        const s = map[field.key];
        if (!s) return null; // key not present in this deployment
        return (
          <div key={field.key} className="py-4 first:pt-0 last:pb-0">
            <SettingField
              field={field}
              setting={s}
              value={currentValue(field)}
              onChange={(v) => setField(field.key, v)}
              disabled={!canUpdate}
              roleOptions={roleOptions}
              modelOptions={modelOptions}
            />
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="flex flex-col gap-6 pb-24">
      <PageHeader
        title={t('nav.settings')}
        description="Configura il sistema con controlli guidati. Le impostazioni tecniche sono nella scheda Avanzate."
      />

      {/* Search */}
      <div className="relative w-full max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-faint" />
        <Input
          className="pl-9"
          placeholder="Cerca un'impostazione..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tabs (hidden while searching) */}
      {!q && (
        <div className="flex flex-wrap gap-1 border-b border-border">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            const active = s.id === activeTab;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveTab(s.id)}
                className={[
                  'inline-flex items-center gap-2 border-b-2 px-3.5 py-2.5 text-sm font-medium transition-colors',
                  active ? 'border-ink text-ink' : 'border-transparent text-ink-muted hover:text-ink'
                ].join(' ')}
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
                {s.label}
              </button>
            );
          })}
        </div>
      )}

      {settingsQuery.isLoading ? (
        <div className="flex flex-col gap-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
      ) : q ? (
        searchResults.length === 0 ? (
          <Card><p className="text-sm text-ink-muted">Nessuna impostazione trovata per "{search}".</p></Card>
        ) : (
          <div className="flex flex-col gap-4">
            {searchResults.map(({ section: s, fields }) => (
              <Card key={s.id}>
                <h2 className="mb-3 text-sm font-semibold text-ink">{s.label}</h2>
                {renderFields(fields)}
              </Card>
            ))}
          </div>
        )
      ) : (
        section.groups ? (
          <div className="flex flex-col gap-4">
            {section.groups.map((g) => (
              <Card key={g.label}>
                <div className="mb-3">
                  <h2 className="text-sm font-semibold text-ink">{g.label}</h2>
                  {g.help && <p className="mt-0.5 text-xs text-ink-muted">{g.help}</p>}
                </div>
                {renderFields(g.fields)}
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            {section.note && (
              <div className="mb-5 flex items-center justify-between gap-3 rounded-md border border-border bg-surface-subtle px-4 py-3">
                <span className="text-sm text-ink-muted">{section.note.text}</span>
                <Link to={section.note.to} className="inline-flex shrink-0 items-center gap-1 text-sm text-brand hover:text-brand-hover">
                  {section.note.linkLabel} <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
            {renderFields(section.fields)}
          </Card>
        )
      )}

      {/* Sticky save bar */}
      {canUpdate && dirtyKeys.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface-elevated/95 backdrop-blur">
          <div className="mx-auto flex max-w-5xl items-center justify-end gap-3 px-6 py-3">
            <span className="mr-auto text-sm text-ink-muted">{dirtyKeys.length} modifica{dirtyKeys.length > 1 ? 'he' : ''} non salvata{dirtyKeys.length > 1 ? 'e' : ''}</span>
            <Button variant="ghost" onClick={() => setDraft({})} disabled={saveMutation.isPending}>Annulla</Button>
            <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>Salva modifiche</Button>
          </div>
        </div>
      )}
    </div>
  );
}
